import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { CreditCard, Shield, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PACKAGES = [
  { id: "small", label: "Standard Bid Bond", amount: 500, desc: "Minimal coverage for small bids" },
  { id: "medium", label: "Premium Bid Bond", amount: 2500, desc: "Standard mid-tier coverage" },
  { id: "large", label: "Retainage Hold", amount: 10000, desc: "Major project retainage account" },
];

export default function BidBond() {
  const [selected, setSelected] = useState("medium");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/history");
        setHistory(data);
      } catch {}
    })();
  }, []);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: selected,
        origin_url: window.location.origin
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto" data-testid="bid-bond-page">
        <div className="font-mono-data text-[10px] tracking-[0.3em] uppercase text-amber-500 mb-2">Treasury</div>
        <h1 className="font-display text-5xl mb-3">Bid Bonds & Retainage</h1>
        <p className="text-text-secondary mb-8">Post a bond or hold retainage for active bids. Powered by Stripe.</p>

        <div className="tray p-6 mb-6">
          <div className="flex items-center gap-3 mb-5">
            <Shield className="w-5 h-5 text-amber-500" />
            <div>
              <div className="font-display text-xl">Select Bond Type</div>
              <div className="font-mono-data text-[10px] text-text-secondary uppercase tracking-widest">Secure · Encrypted · Stripe</div>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 mb-6">
            {PACKAGES.map(p => (
              <button
                key={p.id}
                onClick={() => setSelected(p.id)}
                data-testid={`bond-${p.id}-btn`}
                className={`tray-inset p-4 text-left transition ${selected === p.id ? "ring-2 ring-amber-500" : ""}`}
              >
                <div className="font-display text-2xl text-amber-500">${p.amount.toLocaleString()}</div>
                <div className="font-mono-data text-xs mt-1 text-white">{p.label}</div>
                <div className="text-[11px] text-text-secondary mt-1">{p.desc}</div>
              </button>
            ))}
          </div>

          <button onClick={checkout} disabled={loading} data-testid="checkout-btn"
            className="btn-tactile btn-amber w-full inline-flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            Pay via Stripe Checkout
          </button>
        </div>

        {history.length > 0 && (
          <div className="tray p-6" data-testid="payment-history">
            <div className="font-display text-xl mb-4">Payment History</div>
            <div className="space-y-2">
              {history.map(tx => (
                <div key={tx.session_id} className="tray-inset p-3 flex justify-between items-center">
                  <div>
                    <div className="font-mono-data text-sm">${tx.amount} {tx.currency.toUpperCase()}</div>
                    <div className="font-mono-data text-[10px] text-text-secondary">{tx.session_id.slice(0, 16)}...</div>
                  </div>
                  <span className={`stamp ${tx.payment_status === "paid" ? "stamp-ok" : "stamp-warn"}`}>
                    {tx.payment_status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
